import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Auth, signOut, User as FirebaseUser, signInWithEmailAndPassword } from '@angular/fire/auth'
import { Router } from '@angular/router'

import { finalize, from, map, Observable, switchMap, tap, throwError } from 'rxjs'

import { UserRole } from '@yoshu-lib/constants/roles.enum'

import { User } from '@yoshu-lib/models/user.model'

import { TranslateService } from '@ngx-translate/core'

import { AUTH_STORE } from '../tokens/auth-store.token'
import { ENVIRONMENT } from '../tokens/environment.token'

@Injectable({ providedIn: 'root' })
export class AuthService {
  private env = inject(ENVIRONMENT)
  private auth = inject(Auth)
  private authStore = inject(AUTH_STORE)
  private router = inject(Router)
  private http = inject(HttpClient)
  private translate = inject(TranslateService)

  signInWithGoogle(email: string, password: string) {
    if (email.endsWith('@yoshu.ai')) {
      this.auth.tenantId = null
    } else this.auth.tenantId = this.env.tenantId
    this.authStore.setIsLoggingIn(true)

    return from(
      signInWithEmailAndPassword(this.auth, email, password).then((res) => res.user)
    ).pipe(finalize(() => this.authStore.setIsLoggingIn(false)))
  }

  loginToYoshuApi(firebaseUser: FirebaseUser) {
    return from(firebaseUser.getIdToken()).pipe(
      switchMap((idToken) => {
        return this.http
          .post<User>(`${this.env.apiUrl}/auth/login/`, { firebase_id_token: idToken })
          .pipe(
            tap((user) => {
              // this.authStore.setUser({ ...user, role: UserRole.User })
              this.authStore.setUser(user)

              const userLang = user.yoshu_language?.split('-')[0]
              const currentLang = this.translate.currentLang

              if (userLang && userLang !== currentLang) {
                this.translate.use(userLang)
              }
            })
          )
      })
    )
  }

  logout(): Observable<void> {
    return from(signOut(this.auth)).pipe(
      tap(() => {
        this.authStore.clearAuth()
        this.router.navigate(['/login'])
      })
    )
  }

  refreshAccessToken() {
    const refresh = this.authStore.refreshToken()

    if (!refresh) {
      this.logout()
      console.warn('No refresh token found. Logging out...')
      return throwError(() => new Error('Missing refresh token'))
    }

    return this.http.post<{ access: string }>(`${this.env.apiUrl}/auth/refresh/`, { refresh }).pipe(
      tap((response) => {
        const newAccessToken = response.access
        this.authStore.updateAccessToken(newAccessToken)
      }),
      map((response) => response.access)
    )
  }
}
